import { startCase } from "lodash";

import type { BadgeProps } from "@alpacahq/ui/old";

import type {
  CryptoAsset,
  CryptoTransfer,
  CryptoWhitelistedAddress,
} from "~/v2/api/rest/cryptoWallet";
import type { Quote } from "~/v2/api/rest/data";
import { Icon } from "~/v2/components/AssetIcon/AssetIcon";
import type { CryptoSelectOption } from "./presentation/CryptoSelect";

const LONG_PREFIX_LEN = 19;
const LONG_SUFFIX_LEN = 3;
const SHORT_PREFIX_LEN = 4;
const SHORT_SUFFIX_LEN = 4;

export const transformAddress = (address: string, isLongVersion?: boolean) => {
  // Transfers and whitelist entries are parsed with `looseArray`, which passes
  // items that fail validation through raw, so `address` can be absent.
  if (!address) {
    return "";
  }
  if (isLongVersion && address.length >= LONG_PREFIX_LEN + LONG_SUFFIX_LEN) {
    return `${address.substring(0, LONG_PREFIX_LEN)}...${address.substring(
      address.length - LONG_SUFFIX_LEN,
    )}`;
  }
  if (address.length >= SHORT_PREFIX_LEN + SHORT_SUFFIX_LEN) {
    return `${address.substring(0, SHORT_PREFIX_LEN)}...${address.substring(
      address.length - SHORT_SUFFIX_LEN,
    )}`;
  }
  return address;
};

export const getTransferStatusBadgeProps = (
  status: string,
): { variant: BadgeProps["variant"]; className?: string } => {
  const upperCaseStatus = status?.toUpperCase();
  switch (upperCaseStatus) {
    case "REJECTED":
    case "FAILED":
      return { variant: "v2/red" };
    case "PENDING":
    case "PROCESSING":
      return { variant: "v2/light-blue" };
    case "COMPLETED":
    case "COMPLETE":
      return { variant: "v2/emerald" };
    case "INPUT_INFORMATION":
      return { variant: "v2/yellow" };
    default:
      return { variant: "v2/gray" };
  }
};

export const getWhitelistStatusBadgeProps = (
  status: string,
): { variant: BadgeProps["variant"]; className?: string } => {
  switch (status) {
    case "APPROVED":
      return { variant: "v2/emerald" };
    case "PENDING":
      return { variant: "v2/light-blue" };
    case "REJECTED":
      return { variant: "v2/red" };
    default:
      return { variant: "v2/gray" };
  }
};

export const getTransactionLink = ({
  transfer,
  asset,
}: {
  transfer: CryptoTransfer;
  asset?: CryptoAsset;
}): string | null => {
  const { chain, tx_hash } = transfer;
  const { assetexplorer } = asset ?? {};

  if (!tx_hash) {
    return null;
  }

  if (assetexplorer) {
    return `${assetexplorer}${tx_hash}`;
  }

  // Backup
  switch (chain) {
    // Ethereum
    case "ETH":
    case "ETH-20":
      return `https://etherscan.io/tx/${tx_hash}`;
    // Bitcoin
    case "BTC":
      return `https://www.blockchain.com/btc/tx/${tx_hash}`;
    // Bitcoin Cash
    case "BCH":
      return `https://www.blockchain.com/bch/tx/${tx_hash}`;
    // Solana
    case "SOL":
    case "USDC_SOL":
    case "USDT_SOL":
      return `https://solscan.io/tx/${tx_hash}`;
    // Ripple
    case "XRP":
      return `https://livenet.xrpl.org/transactions/${tx_hash}`;
    // Dogecoin
    case "DOGE":
      return `https://dogechain.info/tx/${tx_hash}`;
    // Tezos
    case "XTZ":
      return `https://tzstats.com/${tx_hash}`;
    default:
      return null;
  }
};

export const parseCryptoAssetSymbol = (
  symbol: string,
): { chain: string; asset: string } => {
  const [chain, asset] = symbol.split("/");
  return { chain: chain ?? "", asset: asset ?? "" };
};

export const createCryptoAssetSymbol = (asset: string, chain: string) => {
  return `${chain}/${asset}`;
};

export const cryptoAssetSymbolToAssetSymbol = (symbol: string) => {
  return `${getCryptoAssetName(symbol)}USD`;
};

export const cryptoAssetSymbolToQuoteSymbol = (symbol: string) => {
  return `${getCryptoAssetName(symbol)}/USD`;
};

export const getCryptoAssetDisplayName = (chain: string, asset: string) =>
  asset === "NATIVE" ? chain : asset;

export const getCryptoAssetName = (symbol: string) => {
  const { chain, asset } = parseCryptoAssetSymbol(symbol);
  return getCryptoAssetDisplayName(chain, asset);
};

export const getChainIconKey = (chain: string) => (chain ? `${chain}-USD` : "");

export const getAssetIconKey = (chain: string, asset: string) => {
  if (!chain || !asset) {
    return "";
  }
  if (asset === "NATIVE" || asset === chain) {
    return getChainIconKey(chain);
  }
  return asset;
};

export const deriveWalletOwner = (
  entry: CryptoWhitelistedAddress,
): string | null => {
  const tr = entry.travel_rule_info;
  const entity = tr?.beneficiary_entity_name?.trim();
  if (entity) {
    return entity;
  }
  const person = `${tr?.beneficiary_given_name ?? ""} ${
    tr?.beneficiary_family_name ?? ""
  }`.trim();
  return person || null;
};

export const deriveSendingTo = (
  entry: CryptoWhitelistedAddress,
): string | null => {
  if (entry.travel_rule_info?.beneficiary_is_self_hosted === true) {
    return "Self-hosted";
  }
  const type = entry.address_type?.trim();
  return type ? startCase(type.toLowerCase()) : null;
};

export const deriveCustodian = (
  entry: CryptoWhitelistedAddress,
): string | null =>
  entry.address_type === "exchange" ? (entry.institution_name ?? null) : null;

export const createAssetMap = (assets: CryptoAsset[]) =>
  assets.reduce(
    (acc, asset) => {
      const symbol = asset?.symbol ?? "";
      if (symbol) {
        acc[symbol] = asset;
      }
      return acc;
    },
    {} as Record<string, CryptoAsset>,
  );

const formatNameWithCode = (name: string | undefined, code: string) =>
  !name || name === code ? code : `${name} (${code})`;

export const resolveAssetChainCell = ({
  asset,
  chain,
  assetMap,
}: {
  asset: string;
  chain: string;
  assetMap: Record<string, CryptoAsset>;
}): { iconKey: string; assetLabel: string; chainLabel: string } => {
  const symbol = createCryptoAssetSymbol(asset, chain);
  const isNative = asset === "NATIVE" || asset === chain;
  const assetCode = isNative ? chain : asset;
  const iconKey = getAssetIconKey(chain, asset);

  const assetEntry =
    assetMap[symbol] ??
    (isNative ? assetMap[createCryptoAssetSymbol("NATIVE", chain)] : undefined);
  const chainEntry = assetMap[createCryptoAssetSymbol("NATIVE", chain)];

  return {
    iconKey,
    assetLabel: formatNameWithCode(assetEntry?.name, assetCode),
    chainLabel: formatNameWithCode(chainEntry?.name, chain),
  };
};

export const getUSDAmount = ({
  quote,
  amount,
}: {
  quote?: Quote | null;
  amount?: number | null;
}) => {
  if (!quote || !amount) {
    return 0;
  }

  const formatter = new Intl.NumberFormat("us-us", {
    style: "decimal",
    useGrouping: false,
    minimumFractionDigits: 2,
    maximumFractionDigits: 9,
  });

  return formatter.format(quote.last * amount);
};

export interface ChainAssetOptions {
  chainOptions: CryptoSelectOption[];
  assetOptionsByChain: Record<string, CryptoSelectOption[]>;
  chainNameByCode: Record<string, string>;
}

type ValidAsset = CryptoAsset & {
  chain: { chain: string };
  asset: string;
};

const hasChainAndAsset = (asset: CryptoAsset): asset is ValidAsset =>
  !!asset.chain?.chain && !!asset.asset;

const buildChainNameByCode = (assets: CryptoAsset[]) => {
  const map: Record<string, string> = {};
  assets.forEach((asset) => {
    const chain = asset.chain?.chain;
    if (chain && asset.asset === "NATIVE" && asset.name) {
      map[chain] ??= asset.name;
    }
  });
  return map;
};

const makeChainOption = (
  chain: string,
  friendlyName: string | undefined,
): CryptoSelectOption => ({
  value: chain,
  label: friendlyName ? `${friendlyName} (${chain})` : chain,
  icon: <Icon name={getChainIconKey(chain)} />,
});

const makeAssetOption = (asset: ValidAsset): CryptoSelectOption => {
  const displayCode = getCryptoAssetDisplayName(asset.chain.chain, asset.asset);
  return {
    value: asset.asset,
    label: asset.name ? `${asset.name} (${displayCode})` : displayCode,
    icon: <Icon name={getAssetIconKey(asset.chain.chain, asset.asset)} />,
  };
};

export const groupAssetsByChain = (
  assets: CryptoAsset[],
): ChainAssetOptions => {
  const chainNameByCode = buildChainNameByCode(assets);
  const chainOptions: CryptoSelectOption[] = [];
  const assetOptionsByChain: Record<string, CryptoSelectOption[]> = {};

  assets.filter(hasChainAndAsset).forEach((asset) => {
    const chain = asset.chain.chain;

    if (!assetOptionsByChain[chain]) {
      assetOptionsByChain[chain] = [];
      chainOptions.push(makeChainOption(chain, chainNameByCode[chain]));
    }

    assetOptionsByChain[chain].push(makeAssetOption(asset));
  });

  return { chainOptions, assetOptionsByChain, chainNameByCode };
};
